import React from 'react';
import {
    BarChart4, ShoppingBag, HeartPulse, ShoppingCart,
    Factory, Banknote, GraduationCap, Truck, Building
} from 'lucide-react';

const SUCCESS_STORIES = [
    {
        title: "Global Trading Firm",
        challenge: "Massive unstructured data across multiple trading desks preventing quick market analysis",
        solution: "Custom data pipeline with real-time market sentiment analysis",
        result: "45% faster market response time and $2.3M additional quarterly revenue"
    },
    {
        title: "Retail Chain",
        challenge: "Unable to predict seasonal demand across 50+ locations",
        solution: "Advanced predictive analytics platform with machine learning",
        result: "32% reduction in overstock and 28% increase in seasonal item profits"
    },
    {
        title: "Healthcare Provider",
        challenge: "Patient data scattered across departments causing delayed treatment plans",
        solution: "Unified data warehouse with predictive patient care modeling",
        result: "40% faster treatment planning and 25% improvement in patient outcomes"
    },
    {
        title: "E-commerce Platform",
        challenge: "Customer behavior patterns hidden in millions of transactions",
        solution: "Deep analytics engine with customer journey mapping",
        result: "52% increase in customer retention and 38% boost in repeat purchases"
    },
    {
        title: "Manufacturing Group",
        challenge: "Production inefficiencies hidden within years of unanalyzed process data",
        solution: "Custom analytics dashboard with predictive maintenance modeling",
        result: "65% reduction in unplanned downtime and 28% cost savings"
    },
    {
        title: "Financial Services",
        challenge: "Risk patterns buried in decades of transaction data",
        solution: "AI-powered risk analysis platform with real-time monitoring",
        result: "43% improvement in risk assessment accuracy and 31% fewer false positives"
    },
    {
        title: "Education Institution",
        challenge: "Student performance data fragmented across multiple systems",
        solution: "Integrated analytics platform with early warning indicators",
        result: "35% improvement in at-risk student identification and 28% better retention"
    },
    {
        title: "Logistics Company",
        challenge: "Route optimization trapped in complex historical delivery data",
        solution: "Dynamic routing engine with predictive demand modeling",
        result: "23% reduction in fuel costs and 34% improvement in delivery times"
    },
    {
        title: "Real Estate Group",
        challenge: "Market opportunities hidden in vast property and transaction datasets",
        solution: "Market analysis platform with predictive value modeling",
        result: "41% faster property valuations and 27% increase in portfolio performance"
    }
];

// Get icon based on index
const getIcon = (index) => {
    const icons = [
        <BarChart4 className="w-10 h-10" />,
        <ShoppingBag className="w-10 h-10" />,
        <HeartPulse className="w-10 h-10" />,
        <ShoppingCart className="w-10 h-10" />,
        <Factory className="w-10 h-10" />,
        <Banknote className="w-10 h-10" />,
        <GraduationCap className="w-10 h-10" />,
        <Truck className="w-10 h-10" />,
        <Building className="w-10 h-10" />
    ];
    return icons[index];
};

// Function to get unique labels for each success story
const getUniqueLabels = (index) => {
    const labels = [
        { problem: "Unstructured Data", success: "Market Revenue" },
        { problem: "Demand Forecasting", success: "Profit Growth" },
        { problem: "Data Silos", success: "Patient Outcomes" },
        { problem: "Hidden Patterns", success: "Customer Retention" },
        { problem: "Process Inefficiency", success: "Cost Savings" },
        { problem: "Risk Detection", success: "Accuracy Gains" },
        { problem: "Data Fragmentation", success: "Student Retention" },
        { problem: "Route Complexity", success: "Delivery Efficiency" },
        { problem: "Market Analysis", success: "Portfolio Performance" }
    ];
    return labels[index];
};

// Success Story Card Component with pipeline aesthetic and unique labels
const SuccessStoryCard = ({ title, challenge, solution, result, icon, color, index }) => {
    const labels = getUniqueLabels(index);

    return (
        <div className="group relative overflow-hidden rounded-xl transition-all duration-300 hover:translate-y-[-8px]">
            <div className="absolute inset-0 bg-gradient-to-b from-slate-800/90 to-slate-900/90 backdrop-blur-sm shadow-lg" />
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-blue-600/20 transition-opacity duration-500" />
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />

            {/* Pipeline connector on left side */}
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-6 h-20 bg-slate-800 rounded-r-md border border-blue-500/30 overflow-hidden">
                <div className="absolute inset-y-0 right-0 w-1 bg-blue-500/30"></div>
                <div className="absolute top-1/2 left-0 w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
            </div>

            <div className="relative p-6 flex flex-col text-left h-full">
                {/* Removed shadow-lg and shadowColor classes to eliminate glow */}
                <div className="bg-gradient-to-tr from-slate-800 to-slate-700 p-3 rounded-lg border border-slate-600/30 mb-4 self-start">
                    {icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{title}</h3>

                <div className="text-slate-300 leading-relaxed space-y-2">
                    <p><span className="text-blue-400 font-medium">Challenge:</span> {challenge}</p>
                    <p><span className="text-blue-400 font-medium">Solution:</span> {solution}</p>
                    <p><span className="text-blue-400 font-medium">Result:</span> {result}</p>
                </div>

                {/* Data flow indicators at bottom with unique labels */}
                <div className="mt-auto pt-6 w-full">
                    <div className="w-full h-1 bg-slate-700/50 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 w-0 group-hover:w-full transition-all duration-1000 rounded-full"></div>
                    </div>
                    <div className="mt-3 flex justify-between items-center">
                        <div className="flex items-center">
                            <div className="w-2 h-2 bg-blue-400 rounded-full mr-1 animate-pulse"></div>
                            <span className="text-xs text-blue-400">{labels.problem}</span>
                        </div>
                        <div className="flex items-center">
                            <span className="text-xs text-purple-400">{labels.success}</span>
                            <div className="w-2 h-2 bg-purple-400 rounded-full ml-1 animate-pulse"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const SuccessStories = () => {
    return (
        <section className="relative z-10 py-24 px-4 overflow-hidden">
            {/* Decorative background elements */}
            <div className="absolute inset-0 -z-10 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 -left-40 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl"></div>
            </div>

            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-16">
                    {/* Title - Original styling from Success Stories */}
                    <h2 className="text-5xl md:text-6xl font-bold mb-8 tracking-tight">
                        <span className="text-white">Real Solutions </span>
                        <span className="bg-gradient-to-r from-blue-400 via-blue-300 to-purple-400 bg-clip-text text-transparent">
                            Real Results
                        </span>
                    </h2>

                    {/* Description */}
                    <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
                        See how we transform raw data into actionable intelligence that drives business success
                    </p>
                </div>

                {/* Success Stories Grid with updated cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 auto-rows-fr">
                    {SUCCESS_STORIES.map((story, index) => (
                        <SuccessStoryCard
                            key={index}
                            title={story.title}
                            challenge={story.challenge}
                            solution={story.solution}
                            result={story.result}
                            icon={getIcon(index)}
                            color="text-blue-400"
                            index={index}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default SuccessStories;